import type { AvailableApiCategory } from '@pkg/basic'
import type {
  AsterApiType,
  BackpackApiType,
  BinanceApiType,
  BinanceJapanApiType,
  BitbankApiType,
  BitgetApiType,
  BybitApiType,
  GateApiType,
  HibachiApiType,
  HyperliquidApiType,
  MexcApiType,
  SodexApiType,
} from '@pkg/exchange-api'

export const getI18nApiTypeName = (params: {
  category: string
  type: string
}) => {
  const { category, type } = params
  const apiCategory = category as AvailableApiCategory
  switch (apiCategory) {
    case 'aster': {
      const asterType = type as AsterApiType
      switch (asterType) {
        case 'trade-history': {
          return '取引履歴'
        }
        case 'income-history': {
          return '収益履歴'
        }
        default: {
          throw new Error(
            `Unknown Aster API type: ${asterType satisfies never}`,
          )
        }
      }
    }
    case 'backpack': {
      const backpackType = type as BackpackApiType
      switch (backpackType) {
        case 'borrow-lend-history': {
          return '借入・返済履歴'
        }
        case 'capital-history': {
          return '入出金履歴'
        }
        case 'fill-history': {
          return '約定履歴'
        }
        case 'funding-history': {
          return 'ファンディング履歴'
        }
        case 'interest-history': {
          return '利息履歴'
        }
        case 'realized-pnl-history': {
          return '実現損益履歴'
        }
        default: {
          throw new Error(
            `Unknown Backpack API type: ${backpackType satisfies never}`,
          )
        }
      }
    }
    case 'binance': {
      const binanceType = type as BinanceApiType
      switch (binanceType) {
        case 'convert-history': {
          return 'コンバート履歴'
        }
        case 'deposit-history': {
          return '入金履歴'
        }
        case 'spot-trade-history': {
          return '現物取引履歴'
        }
        case 'usd-future-history': {
          return 'USD-M 先物履歴'
        }
        case 'withdrawal-history': {
          return '出金履歴'
        }
        default: {
          throw new Error(
            `Unknown Binance API type: ${binanceType satisfies never}`,
          )
        }
      }
    }
    case 'binance-japan': {
      const binanceJapanType = type as BinanceJapanApiType
      switch (binanceJapanType) {
        case 'convert-history': {
          return 'コンバート履歴'
        }
        case 'deposit-history': {
          return '入金履歴'
        }
        case 'spot-trade-history': {
          return '現物取引履歴'
        }
        case 'withdrawal-history': {
          return '出金履歴'
        }
        default: {
          throw new Error(
            `Unknown Binance Japan API type: ${binanceJapanType satisfies never}`,
          )
        }
      }
    }
    case 'bitbank': {
      const bitbankType = type as BitbankApiType
      switch (bitbankType) {
        case 'deposit-history': {
          return '入金履歴'
        }
        case 'trade-history': {
          return '約定履歴'
        }
        case 'withdrawal-history': {
          return '出金履歴'
        }
        default: {
          throw new Error(
            `Unknown Bitbank API type: ${bitbankType satisfies never}`,
          )
        }
      }
    }
    case 'bitget': {
      const bitgetType = type as BitgetApiType
      switch (bitgetType) {
        case 'deposit-history': {
          return '入金履歴'
        }
        case 'spot-tax-history': {
          return '現物履歴'
        }
        case 'withdrawal-history': {
          return '出金履歴'
        }
        default: {
          throw new Error(
            `Unknown Bitget API type: ${bitgetType satisfies never}`,
          )
        }
      }
    }
    case 'bybit': {
      const bybitType = type as BybitApiType
      switch (bybitType) {
        case 'deposit-history': {
          return '入金履歴'
        }
        case 'future-linear-history': {
          return 'USDC/USDT 先物履歴 (実現損益)'
        }
        case 'spot-trade-history': {
          return '現物取引履歴'
        }
        case 'uta-transaction-history': {
          return 'UTA取引履歴'
        }
        case 'withdrawal-history': {
          return '出金履歴'
        }
        default: {
          throw new Error(
            `Unknown Bybit API type: ${bybitType satisfies never}`,
          )
        }
      }
    }
    case 'gate': {
      const gateType = type as GateApiType
      switch (gateType) {
        case 'deposit-history': {
          return '入金履歴'
        }
        case 'futures-change-history': {
          return '先物履歴'
        }
        case 'spot-trade-history': {
          return '現物取引履歴'
        }
        case 'withdrawal-history': {
          return '出金履歴'
        }
        default: {
          throw new Error(`Unknown Gate API type: ${gateType satisfies never}`)
        }
      }
    }
    case 'hibachi': {
      const hibachiType = type as HibachiApiType
      switch (hibachiType) {
        case 'capital-history': {
          return '入出金履歴'
        }
        case 'trade-history': {
          return '取引履歴'
        }
        default: {
          throw new Error(
            `Unknown Hibachi API type: ${hibachiType satisfies never}`,
          )
        }
      }
    }
    case 'hyperliquid': {
      const hyperliquidType = type as HyperliquidApiType
      switch (hyperliquidType) {
        case 'fill-history': {
          return '約定履歴'
        }
        case 'funding-history': {
          return '資金調達履歴'
        }
        case 'ledger-update-history': {
          return '資産変化履歴'
        }
        case 'staking-reward-history': {
          return 'ステーキング報酬履歴'
        }
        case 'twap-fill-history': {
          return 'TWAP約定履歴'
        }
        default: {
          throw new Error(
            `Unknown Hyperliquid API type: ${hyperliquidType satisfies never}`,
          )
        }
      }
    }
    case 'mexc': {
      const mexcType = type as MexcApiType
      switch (mexcType) {
        case 'deposit-history': {
          return '入金履歴'
        }
        case 'spot-trade-history': {
          return '現物取引履歴'
        }
        case 'withdrawal-history': {
          return '出金履歴'
        }
        default: {
          throw new Error(`Unknown MEXC API type: ${mexcType satisfies never}`)
        }
      }
    }
    case 'sodex': {
      const sodexType = type as SodexApiType
      switch (sodexType) {
        case 'perps-funding-history': {
          return '先物ファンディング履歴'
        }
        case 'perps-trade-history': {
          return '先物約定履歴'
        }
        case 'spot-trade-history': {
          return '現物約定履歴'
        }
        default: {
          throw new Error(
            `Unknown Sodex API type: ${sodexType satisfies never}`,
          )
        }
      }
    }
    default: {
      throw new Error(
        `Unsupported API category: ${apiCategory satisfies never}`,
      )
    }
  }
}

export const getI18nApiTypeDescription = (params: {
  category: string
  type: string
}) => {
  const { category, type } = params
  const apiCategory = category as AvailableApiCategory
  switch (apiCategory) {
    case 'aster': {
      const asterType = type as AsterApiType
      switch (asterType) {
        case 'trade-history': {
          return '- 現物・先物両方の取引履歴が取得されます'
        }
        case 'income-history': {
          return '- 先物の資金調達手数料、手数料、実現損益などの収益履歴が取得されます'
        }
        default: {
          throw new Error(
            `Unknown Aster API type: ${asterType satisfies never}`,
          )
        }
      }
    }
    case 'backpack': {
      const backpackType = type as BackpackApiType
      switch (backpackType) {
        case 'borrow-lend-history': {
          return '- Borrow による借入・返済履歴が取得されます\n- 返済額のうち借入額を超えた分 (借入利息) は支払利息として計上されます'
        }
        case 'capital-history': {
          return '- 入金履歴・出金履歴が取得されます\n- 出金時の手数料も取得されます'
        }
        case 'fill-history': {
          return '- 現物・無期限先物両方の約定履歴が取得されます\n- 現物は取引内容、無期限先物は手数料が記録されます'
        }
        case 'funding-history': {
          return '- 無期限先物のファンディング受取・支払履歴が取得されます'
        }
        case 'interest-history': {
          return '- Lend ポジションの利息受取履歴が取得されます\n- 先物未実現損益に対する利息も含まれます\n- Borrow の借入利息は借入・返済履歴の返済時に計上されます'
        }
        case 'realized-pnl-history': {
          return '- 無期限先物の実現損益が取得されます\n- 手数料とファンディングは含まれません (約定履歴・ファンディング履歴で取得されます)'
        }
        default: {
          throw new Error(
            `Unknown Backpack API type: ${backpackType satisfies never}`,
          )
        }
      }
    }
    case 'binance': {
      const binanceType = type as BinanceApiType
      switch (binanceType) {
        case 'convert-history': {
          return '- コンバート履歴が取得されます'
        }
        case 'deposit-history': {
          return '- 同期期間の入金元ウォレットでの送金取引が自動で「取引所へ入金」として判定されるようになります'
        }
        case 'spot-trade-history': {
          return '- 取引所での現物取引履歴が取得されます'
        }
        case 'usd-future-history': {
          return '- USD-M 先物履歴が取得されます\n- 最大過去3ヶ月(90日間)の履歴が同期可能です。それ以前の履歴は取得できません。'
        }
        case 'withdrawal-history': {
          return '- 出金時の出金手数料が取得されます\n- 同期期間の出金先のウォレットでの受取取引が自動で「取引所から出金」として判定されるようになります'
        }
        default: {
          throw new Error(
            `Unknown Binance API type: ${binanceType satisfies never}`,
          )
        }
      }
    }
    case 'binance-japan': {
      const binanceJapanType = type as BinanceJapanApiType
      switch (binanceJapanType) {
        case 'convert-history': {
          return '- コンバート履歴が取得されます'
        }
        case 'deposit-history': {
          return '- 同期期間の入金元ウォレットでの送金取引が自動で「取引所へ入金」として判定されるようになります'
        }
        case 'spot-trade-history': {
          return '- 取引所での現物取引履歴が取得されます'
        }
        case 'withdrawal-history': {
          return '- 出金時の出金手数料が取得されます\n- 同期期間の出金先のウォレットでの受取取引が自動で「取引所から出金」として判定されるようになります'
        }
        default: {
          throw new Error(
            `Unknown Binance Japan API type: ${binanceJapanType satisfies never}`,
          )
        }
      }
    }
    case 'bitbank': {
      const bitbankType = type as BitbankApiType
      switch (bitbankType) {
        case 'deposit-history': {
          return '- 同期期間の入金元ウォレットでの送金取引が自動で「取引所へ入金」として判定されるようになります'
        }
        case 'trade-history': {
          return '- 取引所での現物取引・信用取引の約定履歴が取得されます\n※ 販売所での売買履歴は取得されません'
        }
        case 'withdrawal-history': {
          return '- 出金時の出金手数料が取得されます\n- 同期期間の出金先のウォレットでの受取取引が自動で「取引所から出金」として判定されるようになります'
        }
        default: {
          throw new Error(
            `Unknown Bitbank API type: ${bitbankType satisfies never}`,
          )
        }
      }
    }
    case 'bitget': {
      const bitgetType = type as BitgetApiType
      switch (bitgetType) {
        case 'deposit-history': {
          return '- 同期期間の入金元ウォレットでの送金取引が自動で「取引所へ入金」として判定されるようになります'
        }
        case 'spot-tax-history': {
          return '- 現物アカウントの取引履歴が取得されます\n- Bitget Earn の報酬受取歴も含まれます\n- エアドロップ受取履歴も含まれます\n- 入出金履歴は含まれません /「入金履歴」「出金履歴」の API から取得してください'
        }
        case 'withdrawal-history': {
          return '- 出金時の出金手数料が取得されます\n- 同期期間の出金先のウォレットでの受取取引が自動で「取引所から出金」として判定されるようになります'
        }
        default: {
          throw new Error(
            `Unknown Bitget API type: ${bitgetType satisfies never}`,
          )
        }
      }
    }
    case 'bybit': {
      const bybitType = type as BybitApiType
      switch (bybitType) {
        case 'deposit-history': {
          return '- 同期期間の入金元ウォレットでの送金取引が自動で「取引所へ入金」として判定されるようになります'
        }
        case 'future-linear-history': {
          return '- USDC/USDT 先物取引の実現損益の履歴が取得されます\n- 実現損益にはポジション維持期間の Funding Fee の支払・受取も含まれます\n- 最大過去２年間(730日間)の履歴が同期可能です。それ以前の履歴は取得できません。'
        }
        case 'spot-trade-history': {
          return '- 取引所での現物取引履歴が取得されます\n- 最大過去２年間(730日間)の履歴が同期可能です。それ以前の履歴は取得できません。'
        }
        case 'uta-transaction-history': {
          return '- UTAの取引履歴が取得されます\n- 最大過去２年間(730日間)の履歴が同期可能です。それ以前の履歴は取得できません。\n- 現物取引履歴は含まれません。現物取引履歴の同期には、「現物取引履歴」を使用してください。\n- 先物取引履歴は含まれません。先物取引履歴の同期には、「USDC/USDT 先物履歴 (実現損益)」等を使用してください。'
        }
        case 'withdrawal-history': {
          return '- 出金時の出金手数料が取得されます\n- 同期期間の出金先のウォレットでの受取取引が自動で「取引所から出金」として判定されるようになります'
        }
        default: {
          throw new Error(
            `Unknown Bybit API type: ${bybitType satisfies never}`,
          )
        }
      }
    }
    case 'gate': {
      const gateType = type as GateApiType
      switch (gateType) {
        case 'deposit-history': {
          return '- 同期期間の入金元ウォレットでの送金取引が自動で「取引所へ入金」として判定されるようになります'
        }
        case 'futures-change-history': {
          return '- 先物アカウント関連の取引履歴が取得されます'
        }
        case 'spot-trade-history': {
          return '- 取引所での現物取引履歴が取得されます'
        }
        case 'withdrawal-history': {
          return '- 出金時の出金手数料が取得されます\n- 同期期間の出金先のウォレットでの受取取引が自動で「取引所から出金」として判定されるようになります'
        }
        default: {
          throw new Error(`Unknown Gate API type: ${gateType satisfies never}`)
        }
      }
    }
    case 'hibachi': {
      const hibachiType = type as HibachiApiType
      switch (hibachiType) {
        case 'capital-history': {
          return '- 入金、出金、送金の履歴が取得されます\n- 入金・出金それぞれ最新100件まで取得されます'
        }
        case 'trade-history': {
          return '- 無期限先物の取引履歴が取得されます（実現損益・手数料を含む）'
        }
        default: {
          throw new Error(
            `Unknown Hibachi API type: ${hibachiType satisfies never}`,
          )
        }
      }
    }
    case 'hyperliquid': {
      const hyperliquidType = type as HyperliquidApiType
      switch (hyperliquidType) {
        case 'fill-history': {
          return '- 現物・無期限先物両方の約定履歴が取得されます\n- 最大で最新10,000件の履歴が同期可能です。それ以前の履歴は取得できません。'
        }
        case 'funding-history': {
          return '- 無期限先物の資金調達履歴が取得されます'
        }
        case 'ledger-update-history': {
          return '以下の履歴が取得されます\n- 送金・出金時に発生する手数料\n- Vault の報酬・費用\n- 新規上場コイン等のAirdrop'
        }
        case 'staking-reward-history': {
          return '- HYPE のステーキング報酬受取履歴が取得されます'
        }
        case 'twap-fill-history': {
          return '- TWAP注文の約定履歴が取得されます'
        }
        default: {
          throw new Error(
            `Unknown Hyperliquid API type: ${hyperliquidType satisfies never}`,
          )
        }
      }
    }
    case 'mexc': {
      const mexcType = type as MexcApiType
      switch (mexcType) {
        case 'deposit-history': {
          return '- 同期期間の入金元ウォレットでの送金取引が自動で「取引所へ入金」として判定されるようになります\n- 最大過去90日間の履歴が同期可能です。それ以前の履歴は取得できません。'
        }
        case 'spot-trade-history': {
          return '- 取引所での現物取引履歴が取得されます\n- 最大過去1ヶ月間の履歴が同期可能です。それ以前の履歴は取得できません。'
        }
        case 'withdrawal-history': {
          return '- 出金時の出金手数料が取得されます\n- 同期期間の出金先のウォレットでの受取取引が自動で「取引所から出金」として判定されるようになります\n- 最大過去90日間の履歴が同期可能です。それ以前の履歴は取得できません。'
        }
        default: {
          throw new Error(`Unknown MEXC API type: ${mexcType satisfies never}`)
        }
      }
    }
    case 'sodex': {
      const sodexType = type as SodexApiType
      switch (sodexType) {
        case 'perps-funding-history': {
          return '- 無期限先物のファンディング履歴が取得されます'
        }
        case 'perps-trade-history': {
          return '- 無期限先物の約定に伴う手数料が取得されます'
        }
        case 'spot-trade-history': {
          return '- 現物の約定履歴が取得されます'
        }
        default: {
          throw new Error(
            `Unknown Sodex API type: ${sodexType satisfies never}`,
          )
        }
      }
    }
    default: {
      throw new Error(
        `Unsupported API category: ${apiCategory satisfies never}`,
      )
    }
  }
}
